import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Button from './ui/Button';
import Input from './ui/Input';
import Icon from './AppIcon';

interface TwoFactorAuthVerificationProps {
  method: 'google_authenticator' | 'sms';
  onVerify: (code: string, recoveryCode?: string) => Promise<void>;
  onResendSms?: () => Promise<void>;
  loading?: boolean;
}

const TwoFactorAuthVerification: React.FC<TwoFactorAuthVerificationProps> = ({
  method,
  onVerify,
  onResendSms,
  loading = false,
}) => {
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (useRecoveryCode) {
      if (!recoveryCode || recoveryCode.length < 8) {
        toast.error('Please enter a valid recovery code');
        return;
      }
      await onVerify('', recoveryCode.toUpperCase());
    } else {
      if (!code || code.length !== 6) {
        toast.error('Please enter a valid 6-digit code');
        return;
      }
      await onVerify(code);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Icon name="Shield" size={32} className="text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Two-Factor Authentication Required
        </h3>
        <p className="text-sm text-muted-foreground">
          {method === 'google_authenticator'
            ? 'Enter the 6-digit code from your authenticator app'
            : 'Enter the 6-digit code sent to your phone'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {!useRecoveryCode ? (
          <div>
            <Input
              label={method === 'google_authenticator' ? 'Authenticator Code' : 'SMS Code'}
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              autoFocus
              autoComplete="one-time-code"
            />
            {method === 'sms' && onResendSms && (
              <button
                type="button"
                onClick={onResendSms}
                className="text-sm text-primary hover:underline mt-2"
              >
                Resend SMS Code
              </button>
            )}
          </div>
        ) : (
          <div>
            <Input
              label="Recovery Code"
              type="text"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="XXXXXXXX"
              autoFocus
            />
          </div>
        )}

        <div className="space-y-3">
          <Button
            type="submit"
            variant="default"
            className="w-full"
            loading={loading}
            disabled={useRecoveryCode ? recoveryCode.length < 8 : code.length !== 6}
          >
            Verify
          </Button>

          <button
            type="button"
            onClick={() => setUseRecoveryCode(!useRecoveryCode)}
            className="text-sm text-primary hover:underline w-full"
          >
            {useRecoveryCode
              ? 'Use authentication code instead'
              : 'Use a recovery code instead'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TwoFactorAuthVerification;

